/**
 * The floor how-to's soundtrack: rain on a shed with nothing under it, a ball bouncing
 * away, a rake and a tamper, every block, beam, joist and sheet set down where the picture
 * sets it, a dog digging, the slab poured and struck off, and a marimba in G under the
 * lesson — the channel's third key, after the shed film's F and the workshop's D.
 *
 * Every cue is read off the timing the picture is drawn from (`floorHowTo.ts`,
 * `floorKit.ts`): the tamper on `tampThumps`, the hammer on `joistBlows`, each piece on
 * `touchdown`. `test/film.test.ts` holds them to each other.
 *
 * The music is quiet and long: a bar of arpeggio per chord, four chords round, from the
 * title to the end card, so a viewer reading the lines has something under them and
 * nothing to listen to instead. A glockenspiel marks each chapter's heading, and the last
 * chord lands as the title block does.
 */

import type { Site } from "./film.js";
import {
  AT,
  CARD,
  CHAPTERS,
  CHOOSE,
  DECKING,
  FRAMING,
  JOB,
  OPEN,
  PIERS,
  SKIDS,
  SLABS,
  THREE,
  TITLE,
  WIPES,
  joistBlows,
  lines,
  rakeStrokes,
  tampThumps,
} from "./floorHowTo.js";
import { BLOCKS, LINES, SHEETS, boltsAt, joistLines, touchdown } from "./floorKit.js";
import { lessonSound } from "./howToProps.js";
import type { Mix } from "./sound.js";

export function floorHowToScore(mix: Mix, site: Site): void {
  open(mix, AT.open);
  title(mix, AT.title);
  job(mix, AT.job);
  three(mix, AT.three);
  skids(mix, AT.skids);
  piers(mix, AT.piers);
  slab(mix, AT.slab);
  frame(mix, AT.frame);
  deck(mix, AT.deck);
  choose(mix, AT.choose);
  lessonSound(mix, lines(site), CHAPTERS, WIPES);
  music(mix);
}

/** Where a stage that runs 0 to 1 over `[start, end]` is at `k`, in the scene's seconds. */
function within([start, end]: readonly [number, number], k: number): number {
  return start + k * (end - start);
}

/** Each of `n` pieces landing over a stage, with a thud and a knock. */
function landings(mix: Mix, at: number, stage: readonly [number, number], n: number, overlap: number, opts: { gain?: number; weight?: number; pan?: number } = {}): void {
  for (let i = 0; i < n; i += 1) {
    const when = at + within(stage, touchdown(i, n, overlap));
    mix.thud(when, { gain: opts.gain ?? 0.45, weight: opts.weight ?? 1, pan: opts.pan ?? 0 });
    mix.tap(when, { gain: 0.16, pitch: 0.75, pan: opts.pan ?? 0 });
  }
}

/** A tape pulled across, and its click as it is read. */
function measure(mix: Mix, at: number, [start, end]: readonly [number, number]): void {
  mix.whirr(at + start, (end - start) * 0.45, { gain: 0.25, pan: 0.2 });
  mix.whirr(at + start + (end - start) * 0.5, (end - start) * 0.45, { gain: 0.25, pan: -0.2 });
}

/** Right: two notes of a bell, a fifth apart. */
function right(mix: Mix, at: number): void {
  mix.bell(at, 784, { gain: 0.25, decay: 0.8 });
  mix.bell(at + 0.12, 1175, { gain: 0.22, decay: 1 });
}

// ---- the open ----------------------------------------------------------------------------------

function open(mix: Mix, at: number): void {
  const [rains, rained] = OPEN.rain;
  mix.rattle(at + rains, rained - rains, { gain: 0.18 });
  mix.whoosh(at + rains, rained - rains, { from: 3000, to: 5200, gain: 0.25 });
  // The corner giving: a long creak and a settling thump.
  const [sinks, sunk] = OPEN.sink;
  mix.scrape(at + sinks, sunk - sinks, { gain: 0.5, bright: 0.2, pan: -0.3 });
  mix.thud(at + sunk - 0.1, { gain: 0.6, weight: 1.6, pan: -0.3 });
  // The door, swinging out on its own.
  const [opens, opened] = OPEN.door;
  mix.scrape(at + opens, opened - opens, { gain: 0.3, bright: 0.45 });
  // The ball, off the deck and bouncing away down the slope.
  const [rolls, rolled] = OPEN.ball;
  const drops = rolls + OPEN.ballDrop * (rolled - rolls) * 0.72;
  mix.boing(at + drops, { gain: 0.45, pitch: 1.3 });
  for (let i = 1; i <= 4; i += 1) {
    mix.boing(at + drops + i * 0.33, { gain: 0.4 / i, pitch: 1.3 + i * 0.05, pan: -0.2 * i });
  }
  mix.roll(at + OPEN.chase[0], OPEN.chase[1] - OPEN.chase[0], { gain: 0.2, pan: -0.5 });
  // The builder's hand meeting the builder's face.
  mix.slap(at + OPEN.palm[0] + 0.25, { gain: 0.5, pan: 0.35 });
  const [zooms, zoomed] = OPEN.zoom;
  mix.whoosh(at + zooms, zoomed - zooms, { from: 250, to: 3200, gain: 0.5 });
}

function title(mix: Mix, at: number): void {
  landings(mix, at, TITLE.piers, 9, 2, { gain: 0.25 });
  landings(mix, at, TITLE.beams, 3, 1.6, { gain: 0.3 });
  for (const word of [0.2, 0.5, 1.2]) {
    mix.pop(at + word, { gain: 0.35, pitch: 0.9 });
  }
}

// ---- 1. what the floor does --------------------------------------------------------------------

function job(mix: Mix, at: number): void {
  JOB.arrows.forEach((arrow, i) => mix.whoosh(at + arrow, 0.6, { from: 2400 - i * 400, to: 500, gain: 0.3 }));
  mix.tick(at + JOB.level[0], { gain: 0.4, pitch: 0.8 });
  right(mix, at + JOB.levelTick);
  measure(mix, at, JOB.diagonals);
  right(mix, at + JOB.diagonalsTick);
  const [rains, rained] = JOB.rain;
  mix.rattle(at + rains, rained - rains, { gain: 0.14 });
  mix.whoosh(at + rains, rained - rains, { from: 3200, to: 5400, gain: 0.22 });
  mix.whoosh(at + JOB.air[0] + 1, 4, { from: 300, to: 900, gain: 0.16 });
}

// ---- 2. three ways ------------------------------------------------------------------------------

function three(mix: Mix, at: number): void {
  mix.pour(at + THREE.gravel[0], THREE.gravel[1] - THREE.gravel[0], { gain: 0.3, pan: -0.5 });
  landings(mix, at, THREE.skids, 3, 1.6, { gain: 0.35, pan: -0.5 });
  landings(mix, at, THREE.piers, 9, 2, { gain: 0.2 });
  landings(mix, at, THREE.beams, 3, 1.6, { gain: 0.3 });
  landings(mix, at, THREE.forms, 4, 1.6, { gain: 0.25, pan: 0.5 });
  mix.pour(at + THREE.pour[0], THREE.pour[1] - THREE.pour[0], { gain: 0.45, pan: 0.5 });
  mix.scrape(at + THREE.strike[0], THREE.strike[1] - THREE.strike[0], { gain: 0.4, bright: 0.75, pan: 0.5 });
  THREE.names.forEach((name, i) => mix.pop(at + name, { gain: 0.4, pitch: 1 + i * 0.2, pan: (i - 1) * 0.5 }));
  for (const pan of [-0.5, 0]) {
    mix.slap(at + THREE.frames[0] + 1.6, { gain: 0.3, pan });
  }
  mix.clank(at + THREE.sills[1] - 0.3, { gain: 0.1, base: 2200, pan: 0.5 });
}

// ---- 3. skids ----------------------------------------------------------------------------------

function skids(mix: Mix, at: number): void {
  // The rake, a scrape on every stroke as the picture turns it.
  for (const stroke of rakeStrokes()) {
    mix.scrape(at + stroke, 0.3, { gain: 0.35, bright: 0.9, pan: -0.2 });
  }
  const [spreads, spread] = SKIDS.gravel;
  mix.roll(at + spreads, spread - spreads, { gain: 0.3 });
  mix.pour(at + spreads, spread - spreads, { gain: 0.5 });
  for (const thump of tampThumps()) {
    mix.thud(at + thump, { gain: 0.55, weight: 1.3 });
  }
  landings(mix, at, SKIDS.skids, 3, 1.6, { gain: 0.6, weight: 1.2 });
  SKIDS.labels.forEach((label) => mix.pop(at + label, { gain: 0.3, pitch: 1.3 }));
  measure(mix, at, [SKIDS.tapeA[0], SKIDS.tapeB[1]]);
  mix.boing(at + SKIDS.wrong, { gain: 0.35, pitch: 0.7 });
  mix.scrape(at + SKIDS.shove[0], SKIDS.shove[1] - SKIDS.shove[0], { gain: 0.45, bright: 0.3, pan: 0.5 });
  right(mix, at + SKIDS.right);
  // The dog's tug, the floor giving, and the dog sitting down hard.
  mix.scrape(at + SKIDS.drag[0], SKIDS.drag[1] - SKIDS.drag[0], { gain: 0.6, bright: 0.25, pan: -0.3 });
  mix.boing(at + SKIDS.tumble[0], { gain: 0.4, pitch: 0.85, pan: -0.6 });
  mix.thud(at + SKIDS.tumble[1], { gain: 0.35, weight: 0.7, pan: -0.6 });
}

// ---- 4. piers ----------------------------------------------------------------------------------

function piers(mix: Mix, at: number): void {
  const [strings, strung] = PIERS.strings;
  for (let i = 0; i < 4; i += 1) {
    mix.tap(at + within([strings, strung], i * 0.12 + 0.3 * 0.72), { gain: 0.4, pitch: 0.9 });
  }
  mix.whirr(at + within([strings, strung], 0.5), (strung - strings) * 0.45, { gain: 0.18 });
  // The dog, digging: scratch after scratch, and the clods landing on the builder.
  const [digs, dug] = PIERS.dig;
  for (let u = digs; u < dug; u += 0.2) {
    mix.scrape(at + u, 0.14, { gain: 0.3, bright: 0.6, pan: 0.1 });
  }
  for (let u = digs + 1; u < dug; u += 1 / 2.2) {
    mix.tap(at + u, { gain: 0.18, pitch: 0.55, pan: -0.5 });
  }
  landings(mix, at, PIERS.set, LINES.length * BLOCKS.length, 2, { gain: 0.5, weight: 1.3 });
  mix.slap(at + PIERS.board[1], { gain: 0.3 });
  for (const tap of PIERS.taps) {
    mix.thud(at + tap, { gain: 0.4, weight: 0.8 });
    mix.tap(at + tap, { gain: 0.5, pitch: 0.7 });
  }
  right(mix, at + PIERS.levelTick);
  landings(mix, at, PIERS.beams, 3, 1.6, { gain: 0.6, weight: 1.2 });
  mix.pop(at + PIERS.joints[0] + 0.3, { gain: 0.4, pitch: 1.2 });
  mix.whoosh(at + PIERS.air[0], 5, { from: 300, to: 900, gain: 0.16 });
  measure(mix, at, PIERS.spacing);
}

// ---- 5. a slab ----------------------------------------------------------------------------------

function slab(mix: Mix, at: number): void {
  // The forms, nailed off as the builder works along them (`slabScene`: a blow every 1 / 2.4 s).
  const [forms, formed] = SLABS.forms;
  for (let n = Math.ceil(forms * 2.4); n / 2.4 <= formed + 0.4; n += 1) {
    mix.tap(at + n / 2.4, { gain: 0.5, pan: -0.2 });
  }
  landings(mix, at, SLABS.forms, 4, 1.6, { gain: 0.3 });
  mix.pour(at + SLABS.gravel[0], SLABS.gravel[1] - SLABS.gravel[0], { gain: 0.4 });
  mix.flutter(at + SLABS.barrier[0], SLABS.barrier[1] - SLABS.barrier[0], { gain: 0.4 });
  mix.clank(at + SLABS.mesh[0] + 0.3, { gain: 0.12, base: 1800 });
  mix.clank(at + SLABS.mesh[1] - 0.4, { gain: 0.1, base: 2100 });
  const [pours, poured] = SLABS.pour;
  mix.roll(at + pours - 0.1, poured - pours, { gain: 0.35 });
  mix.pour(at + pours, poured - pours, { gain: 0.9 });
  mix.scrape(at + SLABS.strike[0], SLABS.strike[1] - SLABS.strike[0], { gain: 0.8, bright: 0.75 });
  boltsAt().forEach((_, i, all) => mix.squelch(at + within(SLABS.bolts, touchdown(i, all.length, 2, 0.5)), { gain: 0.5 }));
  // The dog on its way, and lifted clear just in time.
  mix.boing(at + SLABS.scoop[0], { gain: 0.45, pitch: 1.2, pan: 0.5 });
  mix.whoosh(at + SLABS.scoop[0] + 0.1, 0.5, { from: 600, to: 2400, gain: 0.3, pan: 0.5 });
  mix.whoosh(at + SLABS.sealer[0], SLABS.sealer[1] - SLABS.sealer[0], { from: 900, to: 1500, gain: 0.18 });
  landings(mix, at, SLABS.sills, 4, 1.5, { gain: 0.45, weight: 0.9 });
  mix.clank(at + SLABS.sills[1] + 0.2, { gain: 0.12, base: 2200 });
}

// ---- 6. frame the floor, 7. lay the deck --------------------------------------------------------

function frame(mix: Mix, at: number): void {
  landings(mix, at, FRAMING.rims, 2, 1.4, { gain: 0.55, weight: 1.1 });
  const marks = joistLines().length;
  for (let i = 0; i < marks; i += 1) {
    mix.scribble(at + within(FRAMING.marks, touchdown(i, marks, 2, 0.5)), { gain: 0.3 });
  }
  measure(mix, at, FRAMING.onCenter);
  mix.whoosh(at + FRAMING.flip[0], FRAMING.flip[1] - FRAMING.flip[0], { from: 400, to: 1600, gain: 0.35 });
  mix.thud(at + FRAMING.flip[1], { gain: 0.35, weight: 0.9 });
  right(mix, at + FRAMING.flip[1] + 0.8);
  for (const blow of joistBlows()) {
    mix.tap(at + blow, { gain: 0.6, pan: -0.15 });
  }
  measure(mix, at, FRAMING.square);
  right(mix, at + FRAMING.square[1] - 0.2);
  measure(mix, at, FRAMING.diagonals);
  right(mix, at + FRAMING.diagonalsTick);
  mix.whoosh(at + FRAMING.span[0], FRAMING.span[1] - FRAMING.span[0], { from: 500, to: 2000, gain: 0.25 });
}

function deck(mix: Mix, at: number): void {
  SHEETS.forEach((_, i) => {
    const down = at + within(DECKING.deck, touchdown(i, SHEETS.length, 1.4, 1));
    mix.slap(down, { gain: 0.6 });
    mix.whirr(down + 0.15, 0.35, { gain: 0.3, pan: -0.2 });
  });
  mix.pop(at + DECKING.joints[0] + 0.3, { gain: 0.4, pitch: 1.2 });
  measure(mix, at, DECKING.diagonals);
  right(mix, at + DECKING.diagonalsTick);
}

// ---- 8. which one ------------------------------------------------------------------------------

function choose(mix: Mix, at: number): void {
  CHOOSE.words.forEach((word, i) => mix.pop(at + word, { gain: 0.35, pitch: 1 + i * 0.2, pan: (i - 1) * 0.5 }));
  // The ball, dropped in the doorway, bouncing once and staying put.
  mix.boing(at + CHOOSE.drop, { gain: 0.35, pitch: 1.3 });
  mix.boing(at + CHOOSE.drop + 0.22, { gain: 0.15, pitch: 1.35 });
  right(mix, at + CHOOSE.drop + 0.8);
}

// ---- the music ---------------------------------------------------------------------------------

/** The music's span: from the title to the chord that lands with the end card. */
export const MUSIC = {
  start: AT.title,
  /** The last chord, as the title block lands (`CARD`, eased with a spring). */
  end: CARD[0] + 0.72 * (CARD[1] - CARD[0]),
  bars: 124,
};

/** G, E minor, C, D: a bar each, round and round. Roots as MIDI notes, and their chords. */
const CHORDS: readonly (readonly number[])[] = [
  [55, 59, 62, 67],
  [52, 55, 59, 64],
  [48, 52, 55, 60],
  [50, 54, 57, 62],
];

function music(mix: Mix): void {
  const bar = (MUSIC.end - MUSIC.start) / MUSIC.bars;
  const beat = bar / 4;
  for (let b = 0; b < MUSIC.bars; b += 1) {
    const start = MUSIC.start + b * bar;
    const chord = CHORDS[b % CHORDS.length]!;
    // The bass on the downbeat, and the arpeggio up through the chord above it.
    mix.mallet(start, chord[0]! - 12, 0.65, { ring: 1.6 });
    const pattern = [1, 2, 3, 2];
    pattern.forEach((note, i) => {
      mix.mallet(start + beat * (i + 0.5) + (i === 0 ? beat * 0.5 : 0), chord[note]! + 12, 0.45 + (i === 2 ? 0.08 : 0), { pan: (i - 1.5) * 0.2 });
    });
    if (b % 2 === 1) {
      mix.shaker(start + beat * 2.5, 0.35);
    }
  }
  // A glockenspiel up through the key at every chapter's heading.
  for (const chapter of CHAPTERS) {
    [79, 83, 86].forEach((note, i) => mix.glock(chapter.at + 0.05 + i * 0.12, note, 0.4 + i * 0.05, 0.1));
  }
  // The last chord, with the title block.
  for (const note of [43, 55, 59, 62, 67, 71]) {
    mix.mallet(MUSIC.end, note, 0.7, { ring: 2.4 });
  }
  mix.glock(MUSIC.end, 91, 0.5, 0.1);
}
